import { gsap } from 'gsap';

export default class HeroSliderWithLogo {
    constructor(container) {
        this.container = container;
        this.currentIndex = 0;
        this.isAnimating = false;
        
        // Timing constants
        this.animationDuration = 0.5;    // Match DEFAULT_TRANSITION_DURATION
        this.staggerDelay = 0.05;        // Match DEFAULT_STAGGER_DELAY
        this.autoSlideTime = 3000;       // Match DEFAULT_TIMER_TIME
        
        // Swipe tracking
        this.initialX = 0;
        this.initialY = 0;
        this.isSwiping = false;
        this.swipeThreshold = 50; // Minimum distance for swipe
        
        // Cache elements
        this.backgrounds = [...this.container.querySelectorAll('.hero-slider-layered--backgrounds .hero-slider-layered--slide')];
        this.slides = [...this.container.querySelectorAll('.hero-slider-layered--wrapper .hero-slider-layered--slide')];
        this.logoWrapper = this.container.querySelector('.hero-slider-layered--logo');
        this.prevButton = this.container.querySelector('.swiper-prev');
        this.nextButton = this.container.querySelector('.swiper-next');
        
        this.init();
    }

    init() {
        this.setupEventListeners();
        this.setupInitialState();
        this.setContentHeight();
        this.cleanupInlineStyles();
        this.setupResizeHandler();
        this.animateInitialState();
        this.startAutoSlide();
    }

    animateInitialState() {
        // Create timeline for initial animation
        const timeline = gsap.timeline();

        // Animate logo
        const logoContent = this.logoWrapper?.querySelector('.content');
        if (logoContent) {
            timeline.to(logoContent, {
                x: 0,
                opacity: 1,
                duration: this.animationDuration,
                ease: 'power2.out'
            });
        }

        // Animate first slide layers
        const firstSlide = this.slides[0];
        if (firstSlide) {
            const layers = firstSlide.querySelectorAll('[data-slide-layer]');
            timeline.to(layers, {
                x: 0,
                opacity: 1,
                duration: this.animationDuration,
                stagger: this.staggerDelay,
                ease: 'power2.out'
            }, '-=0.25');
        }
    }

    startAutoSlide() {
        this.stopAutoSlide();
        this.autoSlideTimer = setTimeout(() => {
            if (!this.isAnimating) {
                this.slide('next');
            }
        }, this.autoSlideTime);
    }

    stopAutoSlide() {
        clearTimeout(this.autoSlideTimer);
    }

    setupResizeHandler() {
        let resizeTimeout;
        window.addEventListener('resize', () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                this.setContentHeight();
            }, 250); // Debounce resize events
        });
    }

    cleanupInlineStyles() {
        // Remove opacity from all slides except the first one
        this.slides.forEach((slide, index) => {
            if (index !== 0) {
                slide.style.removeProperty('opacity');
            }
        });
    }

    setupInitialState() {
        // Set initial state for backgrounds
        this.backgrounds.forEach((bg, index) => {
            bg.style.zIndex = 0;
            if (index !== 0) {
                bg.style.opacity = 0;
            }
        });

        // Set initial state for content slides
        this.slides.forEach((slide, index) => {
            if (index !== 0) {
                const layers = slide.querySelectorAll('[data-slide-layer]');
                layers.forEach(layer => {
                    layer.style.opacity = 0;
                    layer.style.transform = 'translateX(100px)';
                });
            }
        });
    }

    setContentHeight() {
        // First, reset all content heights to auto to get their natural height
        this.slides.forEach(slide => {
            const content = slide.querySelector('.content');
            if (content) {
                content.style.height = 'auto';
            }
        });

        // Reset logo wrapper content height
        if (this.logoWrapper) {
            const logoContent = this.logoWrapper.querySelector('.content');
            if (logoContent) {
                logoContent.style.height = 'auto';
            }
        }

        // Force a reflow to ensure heights are calculated
        this.container.offsetHeight;

        // Find the maximum height
        let maxHeight = 0;

        // Check logo wrapper content height
        if (this.logoWrapper) {
            const logoContent = this.logoWrapper.querySelector('.content');
            if (logoContent) {
                const height = logoContent.offsetHeight;
                maxHeight = Math.max(maxHeight, height);
            }
        }

        // Check slide content heights
        this.slides.forEach(slide => {
            const content = slide.querySelector('.content');
            if (content) {
                const height = content.offsetHeight;
                maxHeight = Math.max(maxHeight, height);
            }
        });

        // Set the maximum height to all content elements
        if (this.logoWrapper) {
            const logoContent = this.logoWrapper.querySelector('.content');
            if (logoContent) {
                logoContent.style.height = `${maxHeight}px`;
            }
        }

        this.slides.forEach(slide => {
            const content = slide.querySelector('.content');
            if (content) {
                content.style.height = `${maxHeight}px`;
            }
        });
    }

    handleSwipeStart(e) {
        if (this.isAnimating) return;
        
        this.isSwiping = true;
        this.stopAutoSlide();
        
        // Get initial position
        if (e.type === 'touchstart') {
            this.initialX = e.touches[0].clientX;
            this.initialY = e.touches[0].clientY;
        } else {
            this.initialX = e.clientX;
            this.initialY = e.clientY;
        }
    }

    handleSwipeMove(e) {
        if (!this.isSwiping) return;
        
        let currentX, currentY;
        
        if (e.type === 'touchmove') {
            currentX = e.touches[0].clientX;
            currentY = e.touches[0].clientY;
        } else {
            currentX = e.clientX;
            currentY = e.clientY;
        }
        
        const deltaX = this.initialX - currentX;
        const deltaY = this.initialY - currentY;
        
        // Prevent vertical scrolling if horizontal swipe is detected
        if (Math.abs(deltaX) > Math.abs(deltaY)) {
            e.preventDefault();
        }
    }

    handleSwipeEnd(e) {
        if (!this.isSwiping) return;
        
        let currentX;
        if (e.type === 'touchend') {
            currentX = e.changedTouches[0].clientX;
        } else {
            currentX = e.clientX;
        }
        
        const deltaX = this.initialX - currentX;
        
        if (Math.abs(deltaX) > this.swipeThreshold) {
            if (deltaX > 0) {
                this.slide('next');
            } else {
                this.slide('prev');
            }
        }
        
        this.isSwiping = false;
        this.startAutoSlide();
    }

    setupEventListeners() {
        // Mouse events
        this.container.addEventListener('mousedown', this.handleSwipeStart.bind(this));
        this.container.addEventListener('mousemove', this.handleSwipeMove.bind(this));
        this.container.addEventListener('mouseup', this.handleSwipeEnd.bind(this));
        this.container.addEventListener('mouseleave', this.handleSwipeEnd.bind(this));
        
        // Touch events
        this.container.addEventListener('touchstart', this.handleSwipeStart.bind(this), { passive: false });
        this.container.addEventListener('touchmove', this.handleSwipeMove.bind(this), { passive: false });
        this.container.addEventListener('touchend', this.handleSwipeEnd.bind(this));
        
        // Navigation buttons
        this.prevButton.addEventListener('click', (e) => {
            e.preventDefault();
            if (!this.isAnimating) {
                this.stopAutoSlide();
                this.slide('prev');
            }
        });

        this.nextButton.addEventListener('click', (e) => {
            e.preventDefault();
            if (!this.isAnimating) {
                this.stopAutoSlide();
                this.slide('next');
            }
        });

        // Pause auto-slide on hover
        this.container.addEventListener('mouseenter', () => {
            this.stopAutoSlide();
        });

        this.container.addEventListener('mouseleave', () => {
            this.startAutoSlide();
        });
    }

    slide(direction) {
        if (this.isAnimating) return;
        this.isAnimating = true;

        const nextIndex = direction === 'next' 
            ? (this.currentIndex + 1) % this.slides.length 
            : (this.currentIndex - 1 + this.slides.length) % this.slides.length;

        const currentSlide = this.slides[this.currentIndex];
        const nextSlide = this.slides[nextIndex];
        const currentBg = this.backgrounds[this.currentIndex];
        const nextBg = this.backgrounds[nextIndex];

        // Prepare next background
        nextBg.style.zIndex = 1;
        nextBg.style.opacity = 0;

        // Animate backgrounds
        const bgTimeline = gsap.timeline();
        bgTimeline
            .to(currentBg, {
                opacity: 0,
                duration: this.animationDuration,
                ease: 'power2.out'
            })
            .to(nextBg, {
                opacity: 1,
                duration: this.animationDuration,
                ease: 'power2.out'
            }, '-=0.25');

        // Animate content layers
        const currentLayers = currentSlide.querySelectorAll('[data-slide-layer]');
        const nextLayers = nextSlide.querySelectorAll('[data-slide-layer]');
        const xOffset = direction === 'next' ? -50 : 50;

        // Set initial position for next slide layers
        nextLayers.forEach(layer => {
            gsap.set(layer, {
                x: -xOffset,
                opacity: 0
            });
        });

        const contentTimeline = gsap.timeline();
        
        // Animate out current layers
        contentTimeline.to(currentLayers, {
            x: xOffset,
            opacity: 0,
            duration: this.animationDuration,
            stagger: {
                from: "end",
                each: this.staggerDelay
            },
            ease: 'power2.out'
        });

        // Animate in next layers
        contentTimeline.to(nextLayers, {
            x: 0,
            opacity: 1,
            duration: this.animationDuration,
            stagger: this.staggerDelay,
            ease: 'power2.out'
        }, '-=0.25');

        // Update current index and reset animation state
        contentTimeline.call(() => {
            // Reset z-index of the previous background
            currentBg.style.zIndex = 0;
            nextBg.style.zIndex = 0;
            
            this.currentIndex = nextIndex;
            this.isAnimating = false;
            
            // Restart auto-slide timer
            this.startAutoSlide();
        });
    }
}